/* Libs */
import { useState } from 'react';
import {
	isBefore, isToday, isTomorrow, startOfToday, isYesterday
} from 'date-fns';
import { format, formatDistanceToNowStrict } from 'Helpers/dates';
import getCountryName from 'Helpers/countries';

/* Stores */
import useOrdersStore from 'Stores/Orders';
import useModalStore from 'Stores/Modals';

import ContextualMenuWithIcon from 'Components/ContextualMenuWithIcon';
import Checkbox from 'Components/Checkbox';
import Icon from 'Components/Icon';


/* Style */
import './Orders.scss';

const OrderItem = ({
	order, isSelected, handleSelectedOrder, statusActions
}) => {
	const setActiveModal = useModalStore(store => store.setActiveModal);
	const updateStatus = useOrdersStore(store => store.updateStatus);
	const deleteNote = useOrdersStore(store => store.deleteNote);
	const [addressCopied, setAddressCopied] = useState(null);
	const isEtsy = order.source === 'etsy';
	const isSquarespace = order.source === 'squarespace';

	const onChangeStatus = (status) => {
		if (status === 'sent') {
			setActiveModal('sentOrder', { order });
		} else {
			updateStatus(order, status);
		}
	};

	const onOrderClicked = () => {
		if (Util.orderNotFinished(order.status)) {
			setActiveModal('sentOrder', { order });
		}
	};

	const getPhone = () => {
		const messages = order.details.clientMessage;
		const phone = messages && messages.find(message => message.label === 'Téléphone');

		if (!phone || (phone && !phone.value)) {
			return null;
		}

		return phone.value;
	};

	const getBillingName = () => {
		const { billingAddress } = order.details;
		if (isSquarespace) {
			return `${billingAddress.firstName} ${billingAddress.lastName}`;
		}

		if (isEtsy) {
			const { buyer } = order.details;

			if (buyer && buyer.first_name) {
				return `${buyer.first_name}${buyer.last_name ? ` ${buyer.last_name}` : ''}`;
			}
		}

		return '(Invité)';
	};

	const getAddressName = (details) => {
		if (!details) return '';
		if (details.name)	return details.name;

		return `${details.firstName} ${details.lastName}`;
	};

	const getOrderNumber = () => {
		const number = `#${Util.getOrderNumber(order)}`;

		if (isEtsy) {
			return (
				<span className="number etsy">
					<a
						href={`https://www.etsy.com/your/orders?order_id=${order.order_number}`}
						target="_blank"
						rel="noopener noreferrer"
					>
						<Icon isHM name="stock_share2" label="Voir la commande sur Etsy" position="bottom" />
						<span className="order_number">{number}</span>
					</a>
				</span>
			);
		}

		return <span className="number">{ number }</span>;
	};

	const getShippingMethod = shipping => {
		const isExpress = shipping.isCollisimo || shipping.method?.toLowerCase().includes('express') || shipping.method?.toLowerCase().includes('colissimo');
		const getMethod = shipping.method || (shipping.isCollisimo ? 'Colissimo' : 'Livraison standard');

		return (
			<p className={`shipping ${isExpress ? 'express' : ''}`}>
				{ isExpress ? '✉️✨ ' : '' }{ getMethod } ({ parseFloat(shipping.amount.value).toFixed(2) } €)
			</p>
		);
	};

	const getShippingInfos = () => {
		const { trackings } = order.details;
		const tracking = trackings && trackings[0];

		if (!tracking) return 'Missing tracking from API - check on Etsy or Squarespace';

		const { tracking_url: trackingUrlAPI, trackingNumber, carrierName } = tracking;
		const trackingUrl = trackingUrlAPI || `https://www.laposte.fr/outils/suivre-vos-envois?code=${trackingNumber}`;

		return (
			<div className="shipments_info">
				<span className="carrierName">{carrierName}</span>
				<a href={trackingUrl} className="trackingNumber" target="_blank" rel="noopener noreferrer">
					{trackingNumber}
				</a>
			</div>
		);
	};

	const sendEmail = () => {
		const subject = `[Bohostones] Commande n°${Util.getOrderNumber(order)}`;
		window.location.href = `mailto:${order.customer_email}?subject=${subject}`;
	};

	const copyAddress = (e, address) => {
		e.stopPropagation();
		let text = '';
		text += `${Util.decodeHtmlEntities(getAddressName(address))}\n`;
		text += `${Util.decodeHtmlEntities(address.address1)}\n`;

		if (address.address2) {
			text += `${Util.decodeHtmlEntities(address.address2)}\n`;
		}

		const phone = getPhone(order);
		if (phone) {
			text += `${phone}\n`;
		}

		text += isEtsy ? `${Util.decodeHtmlEntities(address.splitFormatted[address.address2 ? 3 : 2])}\n` : `${address.postalCode} ${Util.decodeHtmlEntities(address.city)}\n`;
		text += `${address.country || getCountryName(address.countryCode)}\n`;

		if (navigator.clipboard) {
			navigator.clipboard.writeText(text).then(() => {
				setAddressCopied({ orderid: order.id });
				setTimeout(() => setAddressCopied(null), 2000);
			}, (err) => {
				console.log('Unable to copy', err);
			});
		} else {
			const textArea = document.createElement('textarea');
			textArea.value = text;

			// Avoid scrolling to bottom
			textArea.style.top = '0';
			textArea.style.left = '0';
			textArea.style.position = 'fixed';

			document.body.appendChild(textArea);
			textArea.focus();
			textArea.select();

			try {
				const copy = document.execCommand('copy');

				if (copy) {
					setAddressCopied({ orderid: order.id });
					setTimeout(() => setAddressCopied(null), 2000);
				}
			} catch (err) {
				console.log('Unable to copy', err);
			}

			document.body.removeChild(textArea);
		}
	};

	const displayAddress = (address, phone = null) => {
		if (isEtsy && address.splitFormatted) {
			return (
				<>
					<p className="name">{Util.decodeHtmlEntities(address.splitFormatted[0])}</p>
					{ phone && <p>{ phone }</p>}
					{ address.splitFormatted.map((value, index) => {
						if (index > 0) {
							return <p key={`address-key-value-${value}`}>{Util.decodeHtmlEntities(value)}</p>
						}
					})}
				</>
				
			)
		} else {
			return (
				<>
					<p className="name">{ Util.decodeHtmlEntities(getAddressName(address)) }</p>
					<p>{ Util.decodeHtmlEntities(address.address1) }</p>
					{ address.address2 && <p>{ Util.decodeHtmlEntities(address.address2) }</p> }
					{ phone && <p>{ phone }</p>}
					<p>
						{ isEtsy && address.splitFormatted
							? `${Util.decodeHtmlEntities(address.splitFormatted[address.address2 ? 3 : 2])}`
							: `${address.postalCode} ${Util.decodeHtmlEntities(address.city)}`
						}
					</p>
					<p>{ address.country || getCountryName(address.countryCode) }</p>
				</>
			)
		}
	}

	const displayClientMessage = (messages, isLove = false) => {
		if (!isLove) {
			const clientMessageAsking = messages && messages.find(message => message.label === 'Demande spéciale');
			if (!clientMessageAsking?.value) {
				return null;
			}

			if (clientMessageAsking?.value) {
				return (
					<div className="message">
						<p>{Util.decodeHtmlEntities(clientMessageAsking.value)}</p>
					</div>
				);
			}
		} else {
			const clientMessageLove= messages && messages.find(message => message.label === 'Ajouter un mot d\'amour');

			if (!clientMessageLove?.value) {
				return null;
			}

			return (
				<div className="love">
					<pre>{Util.decodeHtmlEntities(clientMessageLove.value)}</pre>
				</div>
			);
		}

		return null;
	};
	const displayCustomizations = (customizations) => {
		const ignoredLabels = ['', "Ajouter un mot d'amour"];

		const variants = customizations.filter(variant => !ignoredLabels.includes(variant.label) && variant.value !== '');

		return variants && variants.length > 0 && variants.map(variant => (
			<p key={Math.random()}><i>{variant.label}:</i> {variant.value}</p>
		));
	};

	const displayOptions = (customizations) => {
		const acceptedLabels = ['', 'Ajouter un mot d\'amour'];
		const options = customizations.filter(variant => acceptedLabels.includes(variant.label));

		const isGift = options.find(option => option.label === '' && option.value !== '');
		const hasLovedMessage = options.find(option => option.label === 'Ajouter un mot d\'amour' && option.value !== '');

		return (
			<>
				{ isGift && (
					<p><span role="img" aria-label="gift">🎁</span> Cadeau</p>
				)}
				{ hasLovedMessage && (
					<div className="love">
						<pre>{hasLovedMessage.value}</pre>
					</div>
				)}
			</>
		);
	};

	const getSourceImg = () => {
		if (isSquarespace) {
			return <img className="square-img" src="/public/squarespace.svg" alt="Squarespace" />;
		}

		if (isEtsy) {
			return <img className="etsy-img" src="/public/etsy.jpg" alt="Squarespace" />;
		}

		return '';
	};

	const displayIsPaid = () => {
		const { isPaid } = order.details;
		let tpl = '';
		let classes = '';

		if (isPaid !== undefined) {
			tpl = isPaid ? '✔️ Commande payée' : '⚠️ Commande non payée';
			classes = isPaid ? '' : 'warn';
		} else {
			tpl = '⚠️ Paiement à vérifier sur Etsy';
			classes = 'info';
		}

		return <span className={classes}>{tpl}</span>;
	};

	const deleteInternalNote = async (e) => {
		e.stopPropagation();
		await deleteNote(order);
	};

	const getSendDate = (d) => {
		const date = new Date(d);
		if (isBefore(date, startOfToday())) {
			if (isYesterday(date)) {
				return <p className="envoi late">Envoi prévu hier</p>;
			}
			return <p className="envoi late">Envoi prévu { formatDistanceToNowStrict(new Date(date)) }</p>;
		}

		if (isToday(date)) {
			return <p className="envoi">À envoyer d&apos;ici aujourd&apos;hui</p>;
		}

		if (isTomorrow(date)) {
			return <p className="envoi">À envoyer d&apos;ici demain</p>;
		}

		return <p className="envoi">Envoi d&apos;ici le { format(new Date(date), 'PPP') }</p>;
	};

	const actions = [
		{
			label: `${order.details.internalNotes.length > 0 ? 'Modifier la' : 'Ajouter une'} note interne`,
			value: 'note'
		},
		{
			label: 'Mettre en Annulée (only dashboard)',
			value: 'cancel'
		}
	];

	const handleActions = (action) => {
		switch (action) {
			case 'note':
				setActiveModal('addNote', { order });
				break;
			case 'cancel':
				updateStatus(order, 'canceled');
				break;
			default:
				console.log('this action doesnt exist : ', action);
				break;
		}
	};

	return (
		<div
			onClick={() => onOrderClicked()}
			className={`order-item ${isSelected ? 'selected' : ''} ${order.status === 'sent' ? 'sent' : ''}`}
			role="button"
			tabIndex="0"
		>
			<div className="order-item-body">
				<div className="order-left">
					{ handleSelectedOrder && (
						<div
						className="selector"
						onClick={e => e.stopPropagation()}
						role="button"
						tabIndex="0"
						>
							<Checkbox action={() => handleSelectedOrder(order)} choice={{ value: isSelected }} />
						</div>
					)}
					<div className="order-products">
						<div
							className="order-source"
							onClick={e => e.stopPropagation()}
							role="button"
							tabIndex="0"
						>
							<div className="order-name">
								{ getBillingName() }
								<span className="contact" onClick={() => sendEmail(order)} role="button" tabIndex={0}>
									<span role="img" aria-label="email">📧</span> Contacter
								</span>
							</div>
							<div className="order-infos">
								<span className="source-img">{ getSourceImg() }</span>
								<span className="source">{ order.source }</span>
								-
								{ getOrderNumber() }
								-
								<b>{ parseFloat(order.prices.total.value).toFixed(2) } €</b>
							</div>
							{ isEtsy && <div className="etsy-paid">{ displayIsPaid() }</div> }
						</div>

						<div className="products-list">
							{ order.details.products && order.details.products.length > 0 && order.details.products.map(product => (
								<div className="product" key={Math.random()}>
									<div className="product-image">
										<img src={product.imageUrl} alt={product.productName} />
									</div>
									<div className="product-details">
										{/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
										<p className={`name ${product.url ? 'hasUrl' : ''}`} onClick={e => e.stopPropagation()}>
											<a
												href={product.url}
												target="_blank"
												rel="noopener noreferrer"
											>
												{Util.decodeHtmlEntities(product.productName)}
											</a>
										</p>

										<div className="product-infos">
											<p>
												<i>Quantité:</i> <b className={`${product.quantity > 1 ? 'red' : ''}`}>{ product.quantity } { product.quantity > 1 && <span role="img" aria-label="careful">⚠️</span> }</b>
											</p>
											{ product.variantOptions.length > 0 && product.variantOptions.map(variant => (
												<p key={Math.random()}><i>{Util.decodeHtmlEntities(variant.optionName)}:</i> {Util.decodeHtmlEntities(variant.value)}</p>
											))}

											{ product.customizations && displayCustomizations(product.customizations) }
										</div>

										{ product.customizations && displayOptions(product.customizations) }
									</div>
								</div>
							))}
						</div>
					</div>
				</div>

				<div className="order-right">
					<div className="infos">
						{ order.status === 'canceled' && <p className="envoi late">Annulée</p>}
						{ order.status === 'sent' && <p className="envoi">Terminé</p>}
						{ Util.orderNotFinished(order.status) && order.sendDate && getSendDate(order.sendDate) }
						<p className="subtitle-envoi">Commandé le { format(new Date(order.created_on), 'PPP') }</p>

						{ order.status === 'sent' && getShippingInfos() }
						{ Util.orderNotFinished(order.status) && order.details.shippingOptions.length > 0 && getShippingMethod(order.details.shippingOptions[0]) }

						<div className="shipping-address">
							{ order.details.shippingAddress && (
								<>
									<p className="title">
										Adresse de livraison

										{ (!addressCopied || (addressCopied && order.id !== addressCopied.orderid)) && (
											<span onClick={(e) => copyAddress(e, order.details.shippingAddress, order)} className="clipboard" role="button" tabIndex={0}>
												<span role="img" aria-label="clipboard">📋</span> Copier
											</span>
										)}

										{ addressCopied && order.id === addressCopied.orderid && <span className="clipboard"><span role="img" aria-label="clipboard">✔️</span> Copié ! </span> }
									</p>
									{ displayAddress(order.details.shippingAddress, getPhone(order)) }
								</>
							)}
							{ isSquarespace && !order.details.shippingAddress && (
								<div className="click-collect">
									<p className="title">CLICK AND COLLECT</p>
									<p>Boutique Maison Carillo</p>
								</div>
							) }

							
						</div>

						{ isEtsy && (
							<div className="etsy-infos">
								{ order.details.hasAlreadyOrdered && (
									<p><span role="img" aria-label="fidelity">⭐</span> Client(e) fidèle</p>
								)}
								{ order.details.isGift && (
									<p><span role="img" aria-label="gift">🎁</span> Cadeau</p>
								)}
								{ order.details.giftMessage && (
									<div className="love">
										<pre>{Util.decodeHtmlEntities(order.details.giftMessage)}</pre>
									</div>
								)}
							</div>
						)}
						{ isSquarespace && order.details.clientMessage && displayClientMessage(order.details.clientMessage, true) }
					</div>
					{ statusActions && (
						<div className="order-actions">
							<ContextualMenuWithIcon
								icon="utilitary_update"
								label="Mettre à jour l'avancement"
								position="left"
								handleAction={status => onChangeStatus(status)}
								items={statusActions}
							/>
							<ContextualMenuWithIcon
								icon="rounded_roundmore2"
								label="Autres actions"
								position="left"
								handleAction={value => handleActions(value)}
								items={actions}
							/>
						</div>
					)}
				</div>
			</div>

			<div className="order-item-footer">
				{ order.details.clientMessage && displayClientMessage(order.details.clientMessage) }

				{ order.details.internalNotes && order.details.internalNotes.length > 0 && (
					<div className="notes">
						<div className="notes-header">
							<span role="img" aria-label="note">🗒️ Notes internes : </span>
							<Icon
								isHM
								name="utilitary_delete"
								label="Supprimer la note"
								position="left"
								onClick={e => deleteInternalNote(e)}
							/>
						</div>
						{
							order.details.internalNotes.map(note => (
								<p key={Math.random()}>{Util.decodeHtmlEntities(note.content)}</p>
							))
						}
					</div>
				)}
			</div>
		</div>
	);
};

OrderItem.propTypes = {
	order: PropTypes.shape().isRequired,
	statusActions: PropTypes.arrayOf(PropTypes.shape()),
	isSelected: PropTypes.bool,
	handleSelectedOrder: PropTypes.func
};

OrderItem.defaultProps = {
	isSelected: false,
	statusActions: null,
	handleSelectedOrder: null,
};

export default OrderItem;
